/**
 * Webhooks: o do navegador (`fetch` do visitante) e o de backend (chamada à nossa Function).
 *
 * A diferença que importa para quem monta o fluxo:
 *
 *  - **navegador**: a requisição sai da máquina do visitante. Ela aparece no DevTools dele, então
 *    qualquer header aqui é público. Também depende de CORS no destino — é a causa número um de
 *    "não funciona" nesse bloco, e por isso o erro tenta nomear CORS em vez de dizer só "failed".
 *  - **backend**: o navegador manda apenas `nodeId` + variáveis. URL, headers e segredos ficam no
 *    servidor. É onde entra qualquer coisa sensível.
 */

import type { JsonValue, Variables, WebhookRequestSpec } from '@merlin/flow-engine';
import type { CodeResult } from './code.js';

function parseBody(text: string, contentType: string | null): JsonValue {
  if (!text) return null;
  if (contentType && contentType.includes('json')) {
    try {
      return JSON.parse(text) as JsonValue;
    } catch {
      return text;
    }
  }
  return text;
}

function snippet(text: string, max = 200): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

export async function runBrowserWebhook(request: WebhookRequestSpec): Promise<CodeResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), request.timeoutMs);

  const headers: Record<string, string> = {};
  for (const pair of request.headers ?? []) {
    if (pair.key) headers[pair.key] = pair.value;
  }

  const hasBody = request.method !== 'GET' && request.body !== undefined;
  if (hasBody) headers['Content-Type'] = request.contentType;

  try {
    const response = await fetch(request.url, {
      method: request.method,
      headers,
      ...(hasBody ? { body: request.body } : {}),
      signal: controller.signal,
      // Sem cookies do destino: o visitante não deve autenticar acidentalmente numa API de terceiro.
      credentials: 'omit',
      mode: 'cors',
      redirect: 'follow',
    });

    const text = await response.text();
    const value = parseBody(text, response.headers.get('content-type'));

    if (!response.ok) {
      return {
        ok: false,
        error: `O webhook respondeu ${response.status}. ${snippet(text)}`,
      };
    }
    return { ok: true, value };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, error: `O webhook não respondeu em ${request.timeoutMs}ms.` };
    }
    // `fetch` só devolve TypeError genérico para CORS, DNS e offline — dá para sugerir, não afirmar.
    const message = error instanceof Error ? error.message : String(error);
    return {
      ok: false,
      error:
        `Não foi possível chamar ${request.url}. Causa provável: o destino não libera CORS para ` +
        `este site (precisa de Access-Control-Allow-Origin), ou o endereço está inacessível. (${message})`,
    };
  } finally {
    clearTimeout(timer);
  }
}

export interface BackendWebhookOptions {
  apiBase: string;
  publicKey: string;
  nodeId: string;
  variables: Variables;
  sessionId: string;
  timeoutMs?: number;
}

interface BackendWebhookResponse {
  ok?: boolean;
  status?: number;
  body?: JsonValue;
  error?: string;
  message?: string;
  truncated?: boolean;
}

export async function runBackendWebhook(options: BackendWebhookOptions): Promise<CodeResult> {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? 20_000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(
      `${options.apiBase.replace(/\/$/, '')}/runWebhook?key=${encodeURIComponent(options.publicKey)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeId: options.nodeId,
          variables: options.variables,
          sessionId: options.sessionId,
        }),
        signal: controller.signal,
        credentials: 'omit',
        mode: 'cors',
      },
    );

    const payload = (await response.json().catch(() => null)) as BackendWebhookResponse | null;

    if (!response.ok || !payload) {
      const detail = payload?.message ?? payload?.error ?? `HTTP ${response.status}`;
      return { ok: false, error: `Webhook de backend falhou: ${detail}` };
    }

    // O status do destino é repassado pelo backend; tratar >= 400 como falha deixa o fluxo
    // desviar pelo handle de erro em vez de seguir como se tivesse dado tudo certo.
    if (typeof payload.status === 'number' && payload.status >= 400) {
      return {
        ok: false,
        error: `O destino respondeu ${payload.status}. ${snippet(JSON.stringify(payload.body ?? ''))}`,
      };
    }

    return { ok: true, value: payload.body ?? null };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, error: `O webhook de backend não respondeu em ${timeoutMs}ms.` };
    }
    return {
      ok: false,
      error: `Não foi possível falar com o backend do webchat. (${error instanceof Error ? error.message : String(error)})`,
    };
  } finally {
    clearTimeout(timer);
  }
}
